<template>
	<div>
		<section :class="{ 'outer-pad': true, 'not-modal': !modal, 'in-modal': modal }">
			<form class="form normal full">
				<div class="form-inputs-section panel full">
					<h3 class="form-section-header" v-if="showOpeners && !modal">Manage Your Notifications</h3>

					<p class="intro-text" v-if="showOpeners">
						Stay in the loop with the notifications that matter most to you. Choose how you'd like to receive updates.
					</p>

					<div v-if="browserNotificationNotice" class="browser-permission-notice" role="status">
						<strong>{{ browserNotificationNotice.title }}</strong>
						<span>{{ browserNotificationNotice.message }}</span>
					</div>

					<!-- Desktop Grid Header -->
					<div class="notifications-grid-header desktop-only">
						<div class="grid-header-item">Type</div>
						<div class="grid-header-item">Information</div>
						<div class="grid-header-item" v-for="platform in notificationPlatforms" :key="'header-' + platform.key">
							{{ platform.label }} <span v-if="platform.tool_tip" @click="toggleToolTip(platform.key, 'header')" class="tipQ">?</span>
							<p v-if="platform.tool_tip && showToolTip[platform.key + '-header']" class="tipP">
								<span class="close" @click="toggleToolTip(platform.key, 'header')"></span
								><span class="text" v-html="platform.tool_tip" />
							</p>
						</div>
					</div>

					<!-- Notifications Grid -->
					<div class="notifications-grid">
						<div
							v-for="notification in notifications"
							:key="notification.key"
							:class="{ 'notification-row': true, 'has-suboptions': notification.hasSubOptions }">
							<!-- Type Column -->
							<div class="notification-type">
								<h4>{{ notification.label }}</h4>
							</div>

							<!-- Info Column -->
							<div :class="{ 'notification-info': true, expanded: expandedItems[notification.key] }">
								<div class="info-content">
									<p class="info-preview">{{ notification.short_description }}</p>
									<div v-if="notification.full_description" class="info-expanded">
										<p>{{ notification.full_description }}</p>
									</div>
									<button
										v-if="notification.full_description"
										type="button"
										:class="{ 'more-info-btn': true }"
										@click="toggleExpanded(notification.key)">
										{{ expandedItems[notification.key] ? "Less info" : "More info" }}
									</button>
								</div>
							</div>

							<!-- Email Column -->
							<div class="notification-email" v-for="platform in notificationPlatforms" :key="'platform-' + platform.key">
								<div class="switcher-wrapper">
									<label class="mobile-label"
										>{{ platform.label }}
										<span v-if="platform.tool_tip" @click="toggleToolTip(notification.key, platform.key)" class="tipQ">?</span>
									</label>
									<p v-if="platform.tool_tip && showToolTip[notification.key + '-' + platform.key]" class="tipP">
										<span class="close" @click="toggleToolTip(notification.key, platform.key)"></span
										><span class="text" v-html="platform.tool_tip" />
									</p>
									<Switcher
										v-if="
											!!platform.active &&
											!notification.platforms.na.includes(platform.key) &&
											!notification.platforms.mandatory.includes(platform.key) &&
											notificationSettings[notification.key]
										"
										v-model="notificationSettings[notification.key][platform.key]"
										switcherLabel="Off"
										switcherLabel2="On"
										:title="`${notification.label} ${platform.label} Notifications`"
										:labelWidthAuto="true"
										@input="handleBrowserToggle($event, platform.key, notification.key)" />
									<button
										v-if="
											!!platform.active &&
											!notification.platforms.na.includes(platform.key) &&
											!notification.platforms.mandatory.includes(platform.key) &&
											platform.key === 'browser' &&
											notificationSettings[notification.key] &&
											notificationSettings[notification.key][platform.key]
										"
										type="button"
										class="test-notification-btn"
										@click="testPushNotification(notification.key, platform.key)"
										title="Send a test notification">
										Test
									</button>
									<div v-if="platform.key === 'browser' && testClicked[notification.key + '-' + platform.key]" class="test-clicked">
										<span class="close" @click="testClicked[notification.key + '-' + platform.key] = false"></span>
										<div class="test-content">
											<p>
												You should see a notification like this appear, if you don't please check that your operating system
												is allowing your browser to receive notifications...<br /><br />
												<img src="/test-notification.jpg" alt="" class="" />
												<span v-if="os === 'macOS'">
													<br /><br />On Mac, goto System Settings > Notifications, then look for your browser in the
													Application Notifications list, click it then toggle on "Allow notifications"</span
												>
												<span v-if="os === 'Windows'">
													<br /><br />On Windows... <br />Right-click on the Start icon and select Settings from the
													list.<br />

													In the System tab, click on Notifications.<br />
													Under 'Notifications from apps and other senders', enable the switch next to your browser name eg
													Google Chrome, if it isn't already.</span
												>
											</p>
										</div>
									</div>
									<span class="coming-soon" v-if="!platform.active && !notification.platforms.na.includes(platform.key)"
										>Coming Soon</span
									>
									<span class="coming-soon" v-if="notification.platforms.na.includes(platform.key)">N/A</span>
									<span class="coming-soon" v-if="!!platform.active && notification.platforms.mandatory.includes(platform.key)"
										>Required</span
									>
								</div>
							</div>
						</div>

						<!-- Sub-options for notifications that have them -->
						<div
							v-for="notification in notificationsWithSubOptions"
							:key="`${notification.key}-suboptions`"
							class="suboptions-container"
							:class="{ disabled: notificationSettings[notification.key] && notificationSettings[notification.key].email }">
							<div class="suboptions-header">
								<h5>{{ notification.label }} - Saved Searches</h5>
								<p
									v-if="notificationSettings[notification.key] && notificationSettings[notification.key].email"
									class="disabled-note">
									Disable "{{ notification.label }}" notifications above to configure individual searches
								</p>
							</div>

							<div class="suboptions-grid">
								<div v-for="subOption in notification.subOptions" :key="`${notification.key}-${subOption.key}`" class="suboption-row">
									<!-- Sub-option Type -->
									<div class="suboption-type">
										<span class="suboption-name">{{ subOption.name }}</span>
										<span class="suboption-description">{{ subOption.description }}</span>
									</div>

									<!-- Sub-option Info -->
									<div class="suboption-info">
										<span class="search-criteria">{{ subOption.criteria }}</span>
									</div>

									<!-- Sub-option Email -->
									<div class="suboption-email">
										<div class="switcher-wrapper">
											<label class="mobile-label">By Email</label>
											<Switcher
												v-if="
													notificationSettings[notification.key] &&
													notificationSettings[notification.key].subOptions &&
													notificationSettings[notification.key].subOptions[subOption.key]
												"
												v-model="notificationSettings[notification.key].subOptions[subOption.key].email"
												switcherLabel="Off"
												switcherLabel2="On"
												:title="`${subOption.name} Email Notifications`"
												:labelWidthAuto="true"
												:disabled="true" />
										</div>
									</div>

									<!-- Sub-option Browser -->
									<div class="suboption-browser">
										<div class="switcher-wrapper">
											<label class="mobile-label">By Browser</label>
											<Switcher
												v-if="
													notificationSettings[notification.key] &&
													notificationSettings[notification.key].subOptions &&
													notificationSettings[notification.key].subOptions[subOption.key]
												"
												v-model="notificationSettings[notification.key].subOptions[subOption.key].browser"
												switcherLabel="Off"
												switcherLabel2="On"
												:title="`${subOption.name} Browser Notifications`"
												:labelWidthAuto="true"
												:disabled="true" />
											<button
												v-if="
													notificationSettings[notification.key] &&
													notificationSettings[notification.key].subOptions &&
													notificationSettings[notification.key].subOptions[subOption.key] &&
													notificationSettings[notification.key].subOptions[subOption.key].browser &&
													!notificationSettings[notification.key].browser
												"
												type="button"
												class="test-notification-btn"
												@click="testPushNotification(notification.key, subOption.key)"
												title="Send a test notification">
												Test
											</button>
										</div>
									</div>

									<!-- Sub-option Slack -->
									<div class="suboption-slack">
										<div class="slack-wrapper">
											<label class="mobile-label">By Slack</label>
											<span class="coming-soon">Coming Soon</span>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</form>

			<p v-if="modal && modalSources.length > 0">
				You are only seeing notifications specific to this area.
				<router-link to="/account/notifications/" exact>Manage all your notifications here</router-link>.
			</p>
		</section>
	</div>
</template>

<script>
import { ref, computed, onMounted, onBeforeUnmount } from "vue";
import { useNotifications } from "./../../composables/useVueInstance";
import { useVueInstance } from "../../composables/useVueInstance";
import ApiService from "./../../services/api.service";
import Switcher from "./../../components/Switcher.vue";
import general from "../../utils/general";

export default {
	name: "Notifications",
	components: {
		Switcher,
	},
	props: {
		modal: {
			type: Boolean,
			default: false,
		},
		modalSources: {
			type: Array,
			default: () => [],
		},
	},
	setup(props) {
		const { store } = useVueInstance();
		const { warning: toastWarning } = useNotifications();

		// Data properties as refs
		const showToolTip = ref({});
		const testClicked = ref({});
		const notificationSettings = ref({});
		const expandedItems = ref({});
		const notificationPlatforms = ref([]);
		const hasBrowserNotificationOption = ref(false);
		const loaded = ref(false);
		const notifications = ref([]);
		const shownUnsupportedWarning = ref(false);
		let browserPermissionStatus = null;
		const pushNotifications = ref({
			isSupported: false,
			permission: "default",
			status: "Not initialized",
			isSubscribed: false,
			error: null,
			vapidPublicKey: null,
			swRegistration: null,
		});

		// Vuex mutations
		const updateLoadingText = (payload) => store.commit("general/updateLoadingText", payload);

		// Computed properties
		const notificationsWithSubOptions = computed(() => {
			return notifications.value.filter(
				(notification) => notification.hasSubOptions && notification.subOptions && notification.subOptionsPersisted,
			);
		});

		const showOpeners = computed(() => {
			return !props.modal || props.modalSources.length === 0;
		});

		const os = computed(() => {
			return general.getOS();
		});

		const hasBrowserNotificationPlatform = computed(() => {
			return notificationPlatforms.value.some((platform) => platform.key === "browser");
		});

		const browserNotificationNotice = computed(() => {
			if (!hasBrowserNotificationPlatform.value && !hasBrowserNotificationOption.value) return null;

			if (!pushNotifications.value.isSupported) {
				return {
					title: "Browser notifications are not supported",
					message: "Use email notifications for this browser.",
				};
			}

			if (pushNotifications.value.permission === "denied") {
				return {
					title: "Browser notifications are blocked",
					message: "Click the icon in your browser address bar and allow notifications, then turn this setting on again.",
				};
			}

			if (pushNotifications.value.permission === "default") {
				return {
					title: "Browser permission required",
					message: "When you turn on a browser notification, your browser will ask you to allow notifications.",
				};
			}

			return null;
		});

		// Methods
		const toggleToolTip = (notificationKey, platformKey) => {
			const theKey = `${notificationKey}-${platformKey}`;
			showToolTip.value = { ...showToolTip.value, [theKey]: !showToolTip.value[theKey] };
		};

		const toggleExpanded = (key) => {
			expandedItems.value = { ...expandedItems.value, [key]: !expandedItems.value[key] };
		};

		const urlBase64ToUint8Array = (base64String) => {
			const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
			const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
			const rawData = window.atob(base64);
			const outputArray = new Uint8Array(rawData.length);
			for (let i = 0; i < rawData.length; ++i) {
				outputArray[i] = rawData.charCodeAt(i);
			}
			return outputArray;
		};

		const syncBrowserPermissionState = () => {
			if ("Notification" in window) {
				pushNotifications.value.permission = Notification.permission;
			}
		};

		const setBrowserNotificationSetting = (notificationKey, platform, value, subOptionKey = null) => {
			if (subOptionKey) {
				notificationSettings.value[notificationKey].subOptions[subOptionKey][platform] = value;
				return;
			}

			notificationSettings.value[notificationKey][platform] = value;
		};

		const browserPermissionBlockedMessage = () => {
			if (pushNotifications.value.permission === "denied") {
				return "Browser notifications are blocked. Click the icon in your browser address bar and allow notifications.";
			}

			return "Browser notifications were not enabled. Please allow notifications in your browser and try again.";
		};

		const checkBrowserNotificationSettings = ({ showToast = true, persist = false } = {}) => {
			syncBrowserPermissionState();
			if (!pushNotifications.value.isSupported || !loaded.value) return;
			const disabledSettings = {};
			let disabledAny = false;

			for (const notificationKey in notificationSettings.value) {
				if (notificationSettings.value[notificationKey].browser) {
					if (!pushNotifications.value.isSubscribed || pushNotifications.value.permission !== "granted") {
						notificationSettings.value[notificationKey].browser = false;
						disabledSettings[notificationKey] = { browser: false };
						disabledAny = true;
					}
				}
				if (notificationSettings.value[notificationKey].subOptions) {
					for (const subKey in notificationSettings.value[notificationKey].subOptions) {
						if (notificationSettings.value[notificationKey].subOptions[subKey].browser) {
							if (!pushNotifications.value.isSubscribed || pushNotifications.value.permission !== "granted") {
								notificationSettings.value[notificationKey].subOptions[subKey].browser = false;
								disabledAny = true;
							}
						}
					}
				}
			}

			if (disabledAny && showToast) {
				toastWarning(browserPermissionBlockedMessage());
			}

			if (persist && Object.keys(disabledSettings).length) {
				updateNotifications(disabledSettings);
			}
		};

		const sanitizeNotificationUpdate = (settings) => {
			if (!settings || typeof settings !== "object") return null;

			const allowedSources = new Set(notifications.value.map((notification) => notification.key));
			const allowedPlatforms = new Set(notificationPlatforms.value.map((platform) => platform.key));
			const sanitized = {};
			let hasInvalid = false;

			for (const sourceKey in settings) {
				if (!allowedSources.has(sourceKey)) {
					hasInvalid = true;
					continue;
				}

				const sourceSettings = settings[sourceKey];
				if (!sourceSettings || typeof sourceSettings !== "object") {
					hasInvalid = true;
					continue;
				}

				for (const platformKey in sourceSettings) {
					if (platformKey === "subOptions") {
						hasInvalid = true;
						continue;
					}

					if (!allowedPlatforms.has(platformKey)) {
						hasInvalid = true;
						continue;
					}

					if (typeof sourceSettings[platformKey] === "undefined") continue;

					sanitized[sourceKey] = sanitized[sourceKey] || {};
					sanitized[sourceKey][platformKey] = sourceSettings[platformKey];
				}
			}

			if (hasInvalid && !shownUnsupportedWarning.value) {
				toastWarning("Some notification options are not supported yet.");
				shownUnsupportedWarning.value = true;
			}

			return Object.keys(sanitized).length ? sanitized : null;
		};

		const updateNotifications = (settings) => {
			const sanitized = sanitizeNotificationUpdate(settings);
			if (!sanitized) return;

			ApiService.put("notifications/user", sanitized).catch((error) => {
				console.error("Failed to update notifications:", error);
				if (error?.response?.data?.error) {
					toastWarning(error.response.data.error);
				}
			});
		};

		const checkPushSupport = () => {
			pushNotifications.value.isSupported = "Notification" in window;
			if (pushNotifications.value.isSupported) {
				pushNotifications.value.isSupported = "serviceWorker" in navigator && "PushManager" in window;
				syncBrowserPermissionState();
			}
			if (!pushNotifications.value.isSupported) {
				pushNotifications.value.status = "Not supported";
			} else if (pushNotifications.value.permission === "denied") {
				pushNotifications.value.status = "Permission denied";
				pushNotifications.value.error = "Notifications are blocked. Please enable them in your browser settings.";
			} else if (pushNotifications.value.permission === "granted") {
				pushNotifications.value.status = "Permission granted";
			} else {
				pushNotifications.value.status = "Permission not requested";
			}
		};

		const registerServiceWorker = async () => {
			if ("serviceWorker" in navigator) {
				try {
					const registration = await navigator.serviceWorker.register("/sw.js", { scope: "/" });
					if (registration.active) {
						registration.active.postMessage({ type: "CLAIM_CLIENTS" });
					} else if (registration.installing) {
						registration.installing.addEventListener("statechange", (e) => {
							if (e.target.state === "activated") {
								e.target.postMessage({ type: "CLAIM_CLIENTS" });
							}
						});
					}
					pushNotifications.value.swRegistration = registration;
					if (registration.active) {
						registration.active.postMessage({ type: "SKIP_WAITING" });
					}
				} catch (error) {
					console.error("Service Worker registration failed:", error);
					throw new Error("Service Worker registration failed: " + error.message);
				}
			}
		};

		const checkPushSubscription = async () => {
			if (!pushNotifications.value.swRegistration) return;
			try {
				const subscription = await pushNotifications.value.swRegistration.pushManager.getSubscription();
				pushNotifications.value.isSubscribed = !!subscription;
				if (subscription) {
					pushNotifications.value.status = "Subscribed";
				}
			} catch (error) {
				console.error("Error checking subscription:", error);
			}
		};

		const subscribeToPush = async () => {
			if (!pushNotifications.value.swRegistration || !pushNotifications.value.vapidPublicKey) {
				throw new Error("Service worker or VAPID key not available");
			}
			try {
				const convertedVapidKey = urlBase64ToUint8Array(pushNotifications.value.vapidPublicKey);
				const subscription = await pushNotifications.value.swRegistration.pushManager.subscribe({
					userVisibleOnly: true,
					applicationServerKey: convertedVapidKey,
				});
				await ApiService.post("browser-notifications/subscribe", subscription);
				pushNotifications.value.isSubscribed = true;
				pushNotifications.value.status = "Subscribed successfully";
				pushNotifications.value.error = null;
				if (Notification.permission === "granted") {
					new Notification("🎉 Notifications Enabled!", {
						body: "You will now receive browser notifications",
						icon: "/pu-192.png",
					});
				}
			} catch (error) {
				console.error("Failed to subscribe to push notifications:", error);
				pushNotifications.value.error = "Failed to subscribe";
				throw error;
			}
		};

		const requestPushPermission = async () => {
			try {
				pushNotifications.value.error = null;
				syncBrowserPermissionState();
				if (Notification.permission === "denied") {
					pushNotifications.value.error =
						'Notifications are blocked. Please enable them in your browser settings:\n\n1. Click the lock icon in your address bar\n2. Set Notifications to "Allow"\n3. Refresh this page';
					return;
				}
				const permission = await Notification.requestPermission();
				pushNotifications.value.permission = permission;
				if (permission === "granted") {
					await subscribeToPush();
				} else if (permission === "denied") {
					pushNotifications.value.error = "Permission denied. Please enable notifications in your browser settings and refresh the page.";
					pushNotifications.value.status = "Permission denied";
				} else {
					pushNotifications.value.error = "Permission request was dismissed. Please try again.";
				}
			} catch (error) {
				console.error("Error requesting permission:", error);
				pushNotifications.value.error = "Failed to request permission: " + error.message;
			}
		};

		const initializePushNotifications = async () => {
			syncBrowserPermissionState();
			if (!pushNotifications.value.isSupported) {
				pushNotifications.value.status = "Not supported";
				return;
			}
			if (pushNotifications.value.permission === "denied") {
				pushNotifications.value.status = "Permission denied";
				pushNotifications.value.error = "Notifications are blocked. Please enable them in your browser settings and refresh the page.";
				return;
			}
			try {
				const response = await ApiService.get("browser-notifications/vapid-public-key");
				pushNotifications.value.vapidPublicKey = response.data.publicKey;
				await registerServiceWorker();
				await checkPushSubscription();
				pushNotifications.value.status = "Initialized";
			} catch (error) {
				console.error("Failed to initialize push notifications:", error);
				pushNotifications.value.error = "Failed to initialize push notifications";
				pushNotifications.value.status = "Error";
			}
		};

		const initializeBrowserPermissionListener = async () => {
			if (!navigator.permissions || !navigator.permissions.query) return;

			try {
				browserPermissionStatus = await navigator.permissions.query({ name: "notifications" });
				browserPermissionStatus.onchange = async () => {
					syncBrowserPermissionState();

					if (pushNotifications.value.permission === "granted") {
						await initializePushNotifications();
						await checkPushSubscription();
						return;
					}

					checkBrowserNotificationSettings({ persist: true });
				};
			} catch (error) {
				console.warn("Unable to watch browser notification permission:", error);
			}
		};

		const testPushNotification = async (notificationKey, platformKey) => {
			try {
				pushNotifications.value.error = null;
				await ApiService.post("browser-notifications/send-test", {
					title: "🧪 Test Notification",
					body: "This is a test notification from PartnerUp!",
					icon: "/pu-192.png",
					url: "/",
				});
				testClicked.value = { ...testClicked.value, [`${notificationKey}-${platformKey}`]: true };
				setTimeout(() => {
					testClicked.value = { ...testClicked.value, [`${notificationKey}-${platformKey}`]: false };
				}, 10000);
			} catch (error) {
				console.error("Failed to send test notification:", error);
				pushNotifications.value.error = "Failed to send test notification";
			}
		};

		const handleBrowserToggle = async (value, platform, notificationKey, subOptionKey = null) => {
			if (platform !== "browser") {
				if (subOptionKey) {
					updateNotifications({ [notificationKey]: { subOptions: { [subOptionKey]: { [platform]: value } } } });
				} else {
					updateNotifications({ [notificationKey]: { [platform]: value } });
				}
				return;
			}
			if (!value) {
				if (subOptionKey) {
					updateNotifications({ [notificationKey]: { subOptions: { [subOptionKey]: { [platform]: value } } } });
				} else {
					updateNotifications({ [notificationKey]: { [platform]: value } });
				}
				return;
			}
			if (!pushNotifications.value.isSupported) {
				pushNotifications.value.error = "Push notifications are not supported in this browser.";
				toastWarning(pushNotifications.value.error);
				setBrowserNotificationSetting(notificationKey, platform, false, subOptionKey);
				return;
			}
			syncBrowserPermissionState();
			if (pushNotifications.value.permission === "denied") {
				pushNotifications.value.error = browserPermissionBlockedMessage();
				toastWarning(pushNotifications.value.error);
				setBrowserNotificationSetting(notificationKey, platform, false, subOptionKey);
				return;
			}
			try {
				if (pushNotifications.value.permission === "default") {
					await requestPushPermission();
				} else if (pushNotifications.value.permission === "granted" && !pushNotifications.value.isSubscribed) {
					// User has granted permission but isn't subscribed - create subscription
					await subscribeToPush();
				}

				if (pushNotifications.value.permission === "granted" && pushNotifications.value.isSubscribed) {
					if (subOptionKey) {
						updateNotifications({ [notificationKey]: { subOptions: { [subOptionKey]: { [platform]: value } } } });
					} else {
						updateNotifications({ [notificationKey]: { [platform]: value } });
					}
				} else {
					setBrowserNotificationSetting(notificationKey, platform, false, subOptionKey);
					toastWarning(pushNotifications.value.error || browserPermissionBlockedMessage());
				}
			} catch (error) {
				console.error("Error handling browser toggle:", error);
				setBrowserNotificationSetting(notificationKey, platform, false, subOptionKey);
				if (pushNotifications.value.error) {
					toastWarning(pushNotifications.value.error);
				}
			}
		};

		// Initialize data on component mount
		onMounted(async () => {
			checkPushSupport();
			await initializeBrowserPermissionListener();
			await initializePushNotifications();

			try {
				const response = await ApiService.get("notifications/options");
				hasBrowserNotificationOption.value = response.data.platforms.some((platform) => platform.key === "browser");
				const p = [];
				response.data.platforms.forEach((platform) => {
					if (!pushNotifications.value.isSupported && platform.key === "browser") return;
					p.push(platform);
				});
				notificationPlatforms.value = p;

				const n = [];
				const baseNotificationSettings = {};
				response.data.sources.forEach((source) => {
					if (props.modalSources.length && !props.modalSources.includes(source.key)) return;
					n.push(source);
					baseNotificationSettings[source.key] = {};
					response.data.platforms.forEach((platform) => {
						if (!source.platforms.na.includes(platform.key) && !source.platforms.mandatory.includes(platform.key)) {
							const pToUpdate = notificationPlatforms.value.find((pl) => pl.key === platform.key);
							if (pToUpdate && pToUpdate.ignores) {
								pToUpdate.ignores.push(source.key);
							}
						} else {
							baseNotificationSettings[source.key][platform.key] = false;
						}
					});
				});
				notifications.value = n;

				const userResponse = await ApiService.get("notifications/user");
				notificationSettings.value = { ...baseNotificationSettings, ...userResponse.data };

				updateLoadingText();

				setTimeout(() => {
					loaded.value = true;
					checkBrowserNotificationSettings({ showToast: false, persist: true });
				}, 100);
			} catch (error) {
				console.error(error);
				if (error?.response?.data?.error) {
					toastWarning(error.response.data.error);
				}
				updateLoadingText();
			}
		});

		onBeforeUnmount(() => {
			if (browserPermissionStatus) {
				browserPermissionStatus.onchange = null;
			}
		});

		return {
			showToolTip,
			testClicked,
			notificationSettings,
			expandedItems,
			notificationPlatforms,
			notifications,
			notificationsWithSubOptions,
			browserNotificationNotice,
			showOpeners,
			os,
			toggleToolTip,
			toggleExpanded,
			handleBrowserToggle,
			testPushNotification,
		};
	},
};
</script>

<style lang="less" scoped>
@import "./../../assets/css/less/_settings.less";
@import "./../../assets/css/less/_helper.less";

.intro-text {
	margin: 0 0 1rem;
	color: @textBody;
}

.browser-permission-notice {
	display: flex;
	flex-direction: column;
	gap: 0.25rem;
	margin: 0 0 1rem;
	padding: 0.875rem 1rem;
	background: @guidedRoseBgSubtle;
	border: 1px solid @borderRose;
	border-radius: @radiusMd;
	color: @textBody;
	line-height: 1.35;

	strong {
		color: @textWarning;
		font-weight: 600;
	}

	span {
		color: @textBody;
	}
}

.form .form-inputs-section.panel {
	box-shadow: none;
	border-radius: 0;
}

// Mobile-first styles (default)
.desktop-only {
	display: none;
}

.notifications-grid-header {
	display: none;
}

.in-modal {
	padding: 0;
	.form .form-inputs-section.panel {
		padding: 0;
		background: none;
	}
}

a {
	color: @guidedRose;
	text-decoration: underline;
	&:hover {
		color: @guidedRose;
		text-decoration: none;
	}
}

.notifications-grid {
	display: flex;
	flex-direction: column;

	> * {
		margin-bottom: 1.5rem;

		&:last-child {
			margin-bottom: 0;
		}
	}

	// Remove bottom margin from notifications that have sub-options
	> .notification-row.has-suboptions {
		margin-bottom: 0;
	}

	// Remove top margin from sub-options containers
	> .suboptions-container {
		margin-top: 0;
	}
}

.notification-row {
	display: grid;
	grid-template-columns: 1fr;
	gap: 1rem;
	align-items: start;
	padding: 1rem;
	background: @surfacePanel;
	border-radius: @radiusMd;
	border: 1px solid @borderRose;

	&.has-suboptions {
		margin-bottom: 0;
		border-bottom-left-radius: 0;
		border-bottom-right-radius: 0;
		border-bottom: none;
	}

	.notification-type {
		border-bottom: 1px solid @borderRoseSubtle;
		padding-bottom: 1rem;

		h4 {
			margin: 0;
			font-size: 1.2rem;
			font-weight: 600;
			color: @textStrong;
		}
	}

	.notification-info {
		order: 1;
		padding-bottom: 1rem;
		border-bottom: 1px solid @borderRoseSubtle;

		.info-content {
			.info-preview {
				margin: 0 0 0.5rem 0;
				color: @textBody;
				line-height: 1.4;
			}

			.info-expanded {
				max-height: 0;
				overflow: hidden;
				transition: max-height 0.3s ease-out;

				p {
					margin: 0;
					color: @textBody;
					line-height: 1.5;
					white-space: pre-line;
					padding-top: 0.5rem;
				}
			}

			.more-info-btn {
				background: none;
				border: none;
				color: @guidedRose;
				cursor: pointer;
				font-size: 0.85rem;
				text-decoration: underline;
				padding: 0;
				margin-top: 0;
			}
		}

		&.expanded {
			.info-content {
				.info-expanded {
					max-height: 200px;
				}

				.more-info-btn {
					margin-top: 0.75rem;
				}
			}
		}
	}

	.notification-email {
		order: 2;
		padding-bottom: 1rem;
		border-bottom: 1px solid @borderRoseSubtle;
		justify-content: flex-start;
		&:last-child {
			border-bottom: none;
		}
	}

	.notification-browser {
		order: 3;
		justify-content: flex-start;
	}

	.notification-slack {
		order: 4;
		justify-content: flex-start;
	}
}

.notification-email,
.notification-browser,
.notification-slack {
	display: flex;
	align-items: center;
}

.switcher-wrapper,
.slack-wrapper {
	width: 100%;
	text-align: left;
	position: relative;

	.mobile-label {
		display: block;
		font-weight: 600;
		margin-bottom: 0.5rem;
		color: @textStrong;
	}
}

.tipQ {
	color: #fff;
	font-size: 0.8rem;
	cursor: pointer;
	margin-left: 0.25rem;
	background-color: @guidedRose;
	width: 16px;
	height: 16px;
	border-radius: @radiusCircle;
	display: inline-block;
	text-align: center;
	padding-top: 0;
}
.tipP {
	position: absolute;
	background-color: @surfacePanel;
	border: 1px solid @borderRoseSubtle;
	padding: 0.5rem;
	border-radius: @radiusXs;
	.elevation.floatingPanel();
	z-index: 1000;
	max-width: 500px;
	font-size: 0.9rem;
	color: @textStrong;
	margin: 0;
	top: 0;
	text-transform: none;
	font-weight: normal;
	.text {
		color: inherit;
		font-size: inherit;
	}

}

.close {
	display: inline-block;
	width: 2rem;
	aspect-ratio: 1/1;
	height: auto;
	border-radius: @radiusCircle;
	border: none;
	background: white url("../../assets/img/close.svg") center center no-repeat;
	background-size: 45% auto;
	cursor: pointer;
	transition:
		background-color 0.2s ease,
		transform 0.5s ease-in-out;
	position: relative;
	z-index: 2;
	position: absolute;
	top: -15px;
	right: -15px;

	&:hover,
	&:focus-visible {
		background-color: @controlHoverDarkTint;
		color: @textStrong;
		transform: rotate(180deg);
	}

	&:focus {
		outline: none;
	}

	&:focus-visible {
		box-shadow: 0 0 0 2px fade(@blue, 20%);
	}
}

.coming-soon {
	color: @textSubtle;
	font-style: italic;
	font-size: 0.9rem;
	display: inline;
	padding: 0;
}

.test-clicked {
	position: absolute;
	background-color: @surfacePanel;
	border: 1px solid @borderRoseSubtle;
	padding: 0.5rem;
	border-radius: @radiusXs;
	.elevation.floatingPanel();
	z-index: 1000;
	font-size: 0.9rem;
	color: @textStrong;
	margin: 0;
	bottom: 0;
	right: 0;
	width: 76vw;
	max-width: 500px;

	.test-content {
		p {
			margin: 10px 0 0;
			color: @red;
		}
		img {
			max-width: 250px;
			border-radius: @radiusSm;
		}
	}
}

.test-notification-btn {
	background: @guidedRose;
	color: white;
	border: none;
	border-radius: @radiusXs;
	padding: 4px 8px;
	font-size: 0.75rem;
	cursor: pointer;
	margin-left: 0.5rem;
	transition: background-color 0.2s ease;

	&:hover {
		background: darken(@guidedRose, 10%);
	}

	&:active {
		background: darken(@guidedRose, 15%);
	}
}

// Sub-options styling (mobile-first)
.suboptions-container {
	background: @surfaceInputActive;
	border: 1px solid @borderRoseSubtle;
	border-top: none;
	border-bottom-left-radius: @radiusMd;
	border-bottom-right-radius: @radiusMd;
	padding: 1rem;
	margin-bottom: 1.5rem;

	&.disabled {
		opacity: 0.6;

		.suboption-row {
			pointer-events: none;
		}
	}

	.suboptions-header {
		margin-bottom: 1rem;
		padding-bottom: 0.75rem;
		border-bottom: 1px solid @borderRoseSubtle;

		h5 {
			margin: 0 0 0.5rem 0;
			font-size: 1rem;
			font-weight: 600;
			color: @textBody;
			text-transform: uppercase;
			letter-spacing: 0.5px;
		}

		.disabled-note {
			margin: 0;
			font-size: 0.9rem;
			color: @textSubtle;
			font-style: italic;
		}
	}

	.suboptions-grid {
		display: flex;
		flex-direction: column;
		gap: 1rem;
	}

	.suboption-row {
		display: grid;
		grid-template-columns: 1fr;
		gap: 1rem;
		align-items: start;
		padding: 0.75rem;
		background: @surfacePanel;
		border-radius: @radiusSm;
		border: 1px solid @borderRoseSubtle;

		.suboption-type {
			display: flex;
			flex-direction: column;
			gap: 0.25rem;
			border-bottom: 1px solid @borderRoseSubtle;
			padding-bottom: 0.75rem;

			.suboption-name {
				font-weight: 600;
				color: @textStrong;
				font-size: 0.95rem;
			}

			.suboption-description {
				font-size: 0.8rem;
				color: @textBody;
			}
		}

		.suboption-info {
			order: 1;
			padding-bottom: 0.75rem;
			border-bottom: 1px solid @borderRoseSubtle;

			.search-criteria {
				font-size: 0.8rem;
				color: @textSubtle;
				line-height: 1.4;
			}
		}

		.suboption-email {
			order: 2;
			padding-bottom: 0.75rem;
			border-bottom: 1px solid @borderRoseSubtle;
			justify-content: flex-start;
		}

		.suboption-browser {
			order: 3;
			padding-bottom: 0.75rem;
			border-bottom: 1px solid @borderRoseSubtle;
			justify-content: flex-start;
		}

		.suboption-slack {
			order: 4;
			justify-content: flex-start;
		}
	}

	.suboption-email,
	.suboption-browser,
	.suboption-slack {
		display: flex;
		align-items: center;
	}
}

// Push Notifications Panel Styles (mobile-first)
.push-notifications-panel {
	margin-top: 2rem;
	display: none;

	.push-notifications-content {
		display: flex;
		flex-direction: column;
		gap: 1.5rem;
	}

	.push-status-card {
		background: @surfacePanel;
		padding: 1rem;
		border-radius: @radiusMd;
		border: 1px solid @borderRoseSubtle;
		border-left: 4px solid @guidedRose;

		.status-item {
			margin-bottom: 0.5rem;

			&:last-child {
				margin-bottom: 0;
			}

			strong {
				color: @textStrong;
			}
		}

		.status-granted {
			color: @green;
			font-weight: 600;
		}

		.status-denied {
			color: @textError;
			font-weight: 600;
		}

		.status-default {
			color: @textMeta;
			font-weight: 600;
		}
	}

	.push-button-group {
		display: flex;
		gap: 1rem;
		flex-direction: column;

		.btn {
			padding: 12px 20px;
			border: none;
			border-radius: @radiusSm;
			cursor: pointer;
			font-size: 14px;
			font-weight: 600;
			transition: all 0.3s ease;
			width: 100%;

			&.btn-primary {
				background: @guidedRose;
				color: white;

				&:hover:not(:disabled) {
					background: darken(@guidedRose, 10%);
				}

				&:disabled {
					opacity: 0.7;
					cursor: not-allowed;
				}
			}

			&.btn-secondary {
				background: @darkContrast;
				color: white;

				&:hover:not(:disabled) {
					background: darken(@darkContrast, 8%);
				}

				&:disabled {
					opacity: 0.6;
					cursor: not-allowed;
				}
			}
		}
	}

	.push-error {
		background: fade(@textError, 12%);
		color: @textError;
		padding: 1rem;
		border-radius: @radiusSm;
		border-left: 4px solid @textError;

		.error-content {
			white-space: pre-line;
			line-height: 1.5;
		}
	}
}

// Extra small mobile adjustments
@media (max-width: 480px) {
	.notification-row {
		padding: 0.75rem;

		.notification-type h4 {
			font-size: 1.1rem;
		}

		.notification-info .info-content .info-preview {
			font-size: 0.9rem;
		}
	}

	.suboptions-container {
		padding: 0.75rem;

		.suboption-row {
			padding: 0.5rem;
		}
	}
}

// Desktop Styles (768px and above)
@media (min-width: 768px) {
	.desktop-only {
		display: block;
	}

	.notifications-grid-header {
		display: grid;
		grid-template-columns: 1fr 2fr 120px 120px 120px;
		gap: 1rem;
		padding: 1rem 1.5rem;
		border-bottom: 2px solid @borderRoseSubtle;
		margin-bottom: 1rem;

		.grid-header-item {
			font-weight: 600;
			color: @textStrong;
			font-size: 0.9rem;
			text-transform: uppercase;
			letter-spacing: 0.5px;
			.tipP {
				right: 30px;
				top: 30px;
			}
		}
	}

	.notification-row {
		grid-template-columns: 1fr 2fr 120px 120px 120px;
		padding: 1.5rem;

		.notification-type {
			border-bottom: none;
			padding-bottom: 0;

			h4 {
				font-size: 1.1rem;
			}
		}

		.notification-info {
			order: initial;
			padding-bottom: 0;
			border-bottom: none;
		}

		.notification-email {
			order: initial;
			padding-bottom: 0;
			border-bottom: none;
			justify-content: center;
		}

		.notification-browser {
			order: initial;
			justify-content: center;
		}

		.notification-slack {
			order: initial;
			justify-content: center;
		}
	}

	.switcher-wrapper,
	.slack-wrapper {
		.mobile-label {
			display: none;
		}
	}

	.coming-soon {
		padding-top: 0.3rem;
		display: inline-block;
	}

	// Desktop sub-options styling
	.suboptions-container {
		padding: 1rem 1.5rem;

		.suboptions-header {
			padding-bottom: 0;
			border-bottom: none;

			h5 {
				font-size: 0.95rem;
				margin-bottom: 0.25rem;
			}

			.disabled-note {
				font-size: 0.85rem;
			}
		}

		.suboption-row {
			grid-template-columns: 1fr 2fr 120px 120px 120px;
			padding: 1rem;

			.suboption-type {
				border-bottom: none;
				padding-bottom: 0;
			}

			.suboption-info {
				order: initial;
				padding-bottom: 0;
				border-bottom: none;
			}

			.suboption-email {
				order: initial;
				padding-bottom: 0;
				border-bottom: none;
				justify-content: center;
			}

			.suboption-browser {
				order: initial;
				padding-bottom: 0;
				border-bottom: none;
				justify-content: center;
			}

			.suboption-slack {
				order: initial;
				justify-content: center;
			}
		}
	}

	// Desktop push notifications
	.push-notifications-panel {
		.push-status-card {
			padding: 1.5rem;
		}

		.push-button-group {
			flex-direction: row;
			flex-wrap: wrap;

			.btn {
				width: auto;
				min-width: 200px;
			}
		}
	}
}
</style>
